import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const GuessWhoRules = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div className="mb-6 bg-gray-800/80 rounded-2xl border border-gray-700 overflow-hidden shadow-lg">
            {/* Header - Always Visible */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-teal-900/40 to-gray-800 hover:from-teal-900/60 hover:to-gray-700 transition-all"
            >
                <div className="flex items-center gap-3">
                    <span className="text-2xl">📖</span>
                    <h2 className="text-xl font-bold text-teal-400 tracking-wide">
                        {t('GuessWho.rules.title', 'GAME RULES - How to Play')}
                    </h2>
                </div>
                <span className={`text-2xl text-teal-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                    ▼
                </span>
            </button>

            {/* Content - Collapsible */}
            <div
                className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden`}
            >
                <div className="px-6 py-5 text-gray-300 space-y-4 text-sm leading-relaxed">
                    {/* Introducción */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>🎯</span> {t('GuessWho.rules.intro_title', 'Introduction')}
                        </h3>
                        <p>
                            {t('GuessWho.rules.intro_text', 'Guess Who is a deduction and strategy game for 2 players based on the UmaMusume Pretty Derby universe. The goal is to guess which character your opponent chose before they guess yours.')}
                        </p>
                    </section>

                    {/* Preparación */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>⚙️</span> {t('GuessWho.rules.setup_title', 'Game Setup')}
                        </h3>
                        <ol className="list-decimal list-inside space-y-1 ml-4">
                            <li>
                                <strong className="text-teal-300">The Board:</strong> {t('GuessWho.rules.setup_board', 'The system randomly selects 24 characters (Umamusumes) from the database. Both players see exactly the same 24 characters on their board.')}
                            </li>
                            <li>
                                <strong className="text-teal-300">Secret Selection:</strong>
                                <ul className="list-disc list-inside ml-6 mt-1 space-y-0.5">
                                    <li>{t('GuessWho.rules.setup_secret1', 'Each player must choose one of those 24 characters as their Secret Character.')}</li>
                                    <li>{t('GuessWho.rules.setup_secret2', 'Your opponent does not know which one you chose.')}</li>
                                    <li>{t('GuessWho.rules.setup_secret3', 'Once both players confirm their selection ("Ready"), the game begins.')}</li>
                                </ul>
                            </li>
                        </ol>
                    </section>

                    {/* Turnos */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>🔄</span> {t('GuessWho.rules.turns_title', 'Turn System')}
                        </h3>
                        <p>
                            {t('GuessWho.rules.turns_text', 'The game is played in turns. The system randomly decides who goes first. During your turn, the border of your screen will light up green and you will see the "YOUR TURN" indicator. While it is your opponent\'s turn, you must wait.')}
                        </p>
                    </section>

                    {/* Acciones */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>🎮</span> {t('GuessWho.rules.actions_title', 'Available Actions')}
                        </h3>
                        <p className="mb-2 text-yellow-300 font-semibold">
                            {t('GuessWho.rules.actions_intro', 'On your turn, you have two main actions, but you can only perform ONE to end your turn:')}
                        </p>

                        {/* Flip */}
                        <div className="bg-gray-900/50 p-3 rounded-lg mb-3 border-l-4 border-teal-500">
                            <h4 className="font-bold text-teal-300 mb-1">
                                A. {t('GuessWho.rules.action_flip_title', 'Discard (Flip) - Standard Action')}
                            </h4>
                            <p className="mb-2">{t('GuessWho.rules.action_flip_desc', 'This is the most common action. If you have deduced that your opponent\'s character does NOT have a certain characteristic (e.g., does not have long hair), you can "flip down" (discard) all the cards that do have it.')}</p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li><strong>How:</strong> {t('GuessWho.rules.action_flip_how', 'Click on any card on the board to "flip it" (it will turn gray with a red X).')}</li>
                                <li><strong>End Turn:</strong> {t('GuessWho.rules.action_flip_end', 'After marking the cards you want to discard, press the "END TURN" button to pass the turn to your opponent.')}</li>
                            </ul>
                        </div>

                        {/* Guess */}
                        <div className="bg-gray-900/50 p-3 rounded-lg mb-3 border-l-4 border-yellow-500">
                            <h4 className="font-bold text-yellow-300 mb-1">
                                B. {t('GuessWho.rules.action_guess_title', 'Guess - Risk Action')}
                            </h4>
                            <p className="mb-2">{t('GuessWho.rules.action_guess_desc', 'If you think you know exactly who your opponent\'s secret character is, you can try to guess.')}</p>
                            <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                                <li><strong>How:</strong>
                                    <ol className="list-decimal list-inside ml-6 mt-1">
                                        <li>{t('GuessWho.rules.action_guess_step1', 'Activate "GUESS MODE" (yellow "GUESS" button).')}</li>
                                        <li>{t('GuessWho.rules.action_guess_step2', 'Click on the card of the character you think is the secret.')}</li>
                                        <li>{t('GuessWho.rules.action_guess_step3', 'Confirm your choice.')}</li>
                                    </ol>
                                </li>
                                <li><strong className="text-red-300">Consequences:</strong>
                                    <ul className="list-disc list-inside ml-6 mt-1">
                                        <li className="text-green-300">✅ <strong>If correct:</strong> {t('GuessWho.rules.action_guess_win', 'YOU WIN THE GAME IMMEDIATELY! 🏆')}</li>
                                        <li className="text-red-300">❌ <strong>If wrong:</strong>
                                            <ul className="list-disc list-inside ml-6">
                                                <li>{t('GuessWho.rules.action_guess_fail1', 'You will be notified that you failed.')}</li>
                                                <li>{t('GuessWho.rules.action_guess_fail2', 'The card you guessed will be automatically discarded.')}</li>
                                                <li>{t('GuessWho.rules.action_guess_fail3', 'You lose your turn immediately.')}</li>
                                            </ul>
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>

                        {/* Magic Question */}
                        <div className="bg-gray-900/50 p-3 rounded-lg border-l-4 border-purple-500">
                            <h4 className="font-bold text-purple-300 mb-1">
                                C. {t('GuessWho.rules.action_magic_title', 'Special Tool: Magic Question (🔮)')}
                            </h4>
                            <p className="mb-2">{t('GuessWho.rules.action_magic_desc', 'In addition to the main actions, you have a special button called "Magic Check".')}</p>
                            <ul className="list-disc list-inside ml-4 space-y-1">
                                <li><strong>Purpose:</strong> {t('GuessWho.rules.action_magic_purpose', 'Asks the "referee" (the system) if it is still possible to win the game.')}</li>
                                <li><strong>Use:</strong> {t('GuessWho.rules.action_magic_use', 'Useful if you suspect that you or your opponent may have accidentally discarded the secret character.')}</li>
                                <li><strong>Limit:</strong> {t('GuessWho.rules.action_magic_limit', 'You only have 3 uses per game.')}</li>
                                <li><strong>Results:</strong>
                                    <ul className="list-disc list-inside ml-6 mt-1">
                                        <li>{t('GuessWho.rules.action_magic_result1', '"Both can win": The game continues normally.')}</li>
                                        <li>{t('GuessWho.rules.action_magic_result2', '"Neither can win": Both have made fatal errors (both discarded each other\'s secret).')}</li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Info del Personaje */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>ℹ️</span> {t('GuessWho.rules.info_title', 'Character Information')}
                        </h3>
                        <p className="mb-2">{t('GuessWho.rules.info_intro', 'To help you deduce, you have information available about each character:')}</p>
                        <ol className="list-decimal list-inside ml-4 space-y-1">
                            <li><strong className="text-teal-300">Zoom & Details:</strong> {t('GuessWho.rules.info_zoom', 'Hover your mouse (or hold down on mobile) over any card to see it enlarged.')}</li>
                            <li><strong className="text-teal-300">Known Suits:</strong> {t('GuessWho.rules.info_suits', 'Below your secret character, you will see a list of all the outfits (Skins/Costumes) of that character.')}</li>
                            <li><strong className="text-teal-300">Aptitudes (Rank A):</strong> {t('GuessWho.rules.info_aptitudes', 'You will also see your character\'s natural aptitudes (Track, Distance, Strategy) to answer technical questions.')}</li>
                        </ol>
                    </section>

                    {/* Ganador */}
                    <section>
                        <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <span>🏆</span> {t('GuessWho.rules.winner_title', 'Winner')}
                        </h3>
                        <p className="text-green-300 font-semibold">
                            {t('GuessWho.rules.winner_text', 'The game ends automatically when a player uses the GUESS action and correctly identifies the opponent\'s secret character.')}
                        </p>
                    </section>

                    {/* Footer */}
                    <div className="pt-3 border-t border-gray-700 text-center">
                        <p className="text-yellow-400 font-bold text-base">
                            {t('GuessWho.rules.footer', '¡Buena suerte, Entrenador! 🐴✨')}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GuessWhoRules;
